import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { FriendProvider } from './contexts/FriendContext';
import { ThemeProvider } from './context/ThemeContext';
import { BadgeNotificationProvider } from './contexts/BadgeNotificationContext';
import api from './utils/api';
import PageLoader from './components/layout/PageLoader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast.css';
import './styles/theme-tokens.css';

// TIER 1: INSTANT - Main navigation pages (loaded immediately for instant tab switching)
import Home from './pages/Home';
import Problems from './pages/Problems';
import Challenges from './pages/Challenges';
import Leaderboard from './pages/Leaderboard';
import About from './pages/About';

// TIER 2: FAST - Important pages (lazy but prefetched aggressively)
const ProblemPage = lazy(() => import('./pages/ProblemPage'));
const Profile = lazy(() => import('./pages/Profile'));
const ListPage = lazy(() => import('./pages/ListPage'));
const Subscription = lazy(() => import('./pages/Subscription'));
const ChallengeDetail = lazy(() => import('./pages/ChallengeDetail'));
const ChallengeProblemPage = lazy(() => import('./pages/ChallengeProblemPage'));

// TIER 3: ON-DEMAND - Less frequent pages (loaded only when needed)
const Navbar = lazy(() => import('./components/layout/Navbar'));
const FriendsPage = lazy(() => import('./components/friends/FriendsPage'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ChallengeEdit = lazy(() => import('./pages/ChallengeEdit'));
const SpectatorView = lazy(() => import('./pages/SpectatorView'));
const ProfilePageNew = lazy(() => import('./pages/ProfilePageNew'));
const Submissions = lazy(() => import('./pages/Submissions'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const History = lazy(() => import('./pages/History'));
const ChallengeResults = lazy(() => import('./pages/ChallengeResults'));
const InviteFriend = lazy(() => import('./pages/InviteFriend'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Settings = lazy(() => import('./pages/Settings'));
const FeedbackManagement = lazy(() => import('./pages/FeedbackManagement'));
const BillingHistory = lazy(() => import('./pages/BillingHistory'));
const AdminNotifications = lazy(() => import('./pages/AdminNotifications'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const Docs = lazy(() => import('./pages/Docs'));
const Support = lazy(() => import('./pages/Support'));
const Careers = lazy(() => import('./pages/Careers'));
const API = lazy(() => import('./pages/API'));
const Blog = lazy(() => import('./pages/Blog'));

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const hideNavbar = location.pathname.match(/^\/challenge\/[^/]+\/problem\/[^/]+$/) || location.pathname.match(/^\/problems\/[^/]+$/);

  useEffect(() => {
    // Health check to warm up the server
    api.get('/api/status').catch(() => {
      // Silent fail - server might be starting up
    });
  }, []);

  useEffect(() => {
    // Aggressive prefetching strategy for instant tab switching
    const prefetchPages = () => {
      // TIER 2: Prefetch important pages immediately on idle
      import('./pages/ProblemPage');
      import('./pages/Profile');
      import('./pages/Subscription');
      import('./pages/ChallengeDetail');
      import('./pages/ChallengeProblemPage');
      
      // TIER 3: Prefetch secondary pages after a short delay
      setTimeout(() => {
        import('./pages/ProfilePageNew');
        import('./pages/Settings');
        import('./components/friends/FriendsPage');
      }, 1000);
    };

    // Use requestIdleCallback for better performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchPages, { timeout: 2000 });
    } else {
      setTimeout(prefetchPages, 100);
    }
  }, []);

  useEffect(() => {
    const handleInviteFriend = async () => {
      const params = new URLSearchParams(window.location.search);
      const invitedBy = params.get('invitedBy');
      
      if (!invitedBy || !currentUser || currentUser._id === invitedBy) {
        return;
      }

      try {
        const friendsRes = await api.get('/api/users/friends');
        const friendsArr = Array.isArray(friendsRes.data.friends) ? friendsRes.data.friends : [];
        const alreadyFriend = friendsArr.some(f => 
          f.user._id === invitedBy || f.user?._id === invitedBy
        );
        
        if (alreadyFriend) {
          return;
        }

        // Send and auto-accept friend request
        await api.post(`/api/users/send-friend-request/${invitedBy}`);
        await api.post(`/api/users/respond-friend-request/${invitedBy}/accept`);
        
        if (window.toast) {
          window.toast.success('Friend request accepted! You are now friends!', {
            position: 'top-right',
            autoClose: 5000
          });
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        if (window.toast) {
          window.toast.error('Could not automatically add friend. You can search for them manually.', {
            position: 'top-right',
            autoClose: 3000
          });
        }
      }
    };

    if (currentUser) {
      handleInviteFriend();
    }
  }, [currentUser]);

  return (
    <ThemeProvider>
      <BadgeNotificationProvider>
        <div className="app-container min-h-screen transition-colors duration-300 dark:bg-background-primary bg-background-secondary">
          <FriendProvider>
            {/* Global toast notifications */}
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss={false}
              draggable
              pauseOnHover
              theme="light"
              limit={1}
              enableMultiContainer={false}
              containerId="main-toast-container"
            />
          
          <Suspense fallback={<PageLoader />}>
            {!hideNavbar && <Navbar />}
          </Suspense>
          <main className="main-content">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/problems" element={<Problems />} />
                <Route path="/lists/:listId" element={
                  <ProtectedRoute>
                    <ListPage />
                  </ProtectedRoute>
                } />
                <Route path="/problems/:problemId" element={<ProblemPage />} />
                <Route path="/problems/:problemId/description" element={<ProblemPage />} />
                <Route path="/problems/:problemId/discussion" element={<ProblemPage />} />
                <Route path="/problems/:problemId/solutions" element={<ProblemPage />} />
                <Route path="/problems/:problemId/submissions" element={<ProblemPage />} />
                <Route path="/challenges" element={<Challenges />} />
                <Route path="/challenges/:id" element={<ChallengeDetail />} />
                <Route path="/challenges/:id/results" element={<ChallengeResults />} />
                <Route path="/challenges/:id/edit" element={
                  <ProtectedRoute>
                    <ChallengeEdit />
                  </ProtectedRoute>
                } />
                <Route path="/challenge/:id/problem/:problemId" element={<ChallengeProblemPage />} />
                <Route path="/challenges/:id/spectate" element={
                  <ProtectedRoute>
                    <SpectatorView />
                  </ProtectedRoute>
                } />
                <Route path="/spectator/:id" element={
                  <ProtectedRoute>
                    <SpectatorView />
                  </ProtectedRoute>
                } />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/about" element={<About />} />
                <Route path="/auth-callback" element={<AuthCallback />} />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/profile/:username" element={<ProfilePageNew />} />
                <Route path="/profile/:username/submissions" element={<Submissions />} />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/feedback-management" element={
                  <ProtectedRoute>
                    <FeedbackManagement />
                  </ProtectedRoute>
                } />
                <Route path="/subscription" element={
                  <ProtectedRoute>
                    <Subscription />
                  </ProtectedRoute>
                } />
                <Route path="/billing-history" element={
                  <ProtectedRoute>
                    <BillingHistory />
                  </ProtectedRoute>
                } />
                <Route path="/history" element={<History />} />
                <Route path="/friends" element={
                  <ProtectedRoute>
                    <FriendsPage />
                  </ProtectedRoute>
                } />
                <Route path="/invite-friend/:token" element={<InviteFriend />} />
                <Route path="/admin/notifications" element={
                  <ProtectedRoute>
                    <AdminNotifications />
                  </ProtectedRoute>
                } />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/api" element={<API />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/support" element={<Support />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </FriendProvider>
      </div>
      </BadgeNotificationProvider>
    </ThemeProvider>
  );
}

export default App;